import {
  VERCEL_BLOB_PRELOAD_IDLE_URLS,
  VERCEL_BLOB_PRELOAD_BOOTSTRAP_GATE_URLS,
  VERCEL_BLOB_PRELOAD_LAZY_URLS,
  VERCEL_BLOB_PRELOAD_MANIFEST_FINGERPRINT,
  VERCEL_BLOB_PRELOAD_PRIORITIZED,
} from '../constants/vercelBlobPreloadUrls'
import { warmWorldMapConfig } from '../game/world-map/worldMapPreload'
import {
  preloadBlobUrlsWithProgress,
  isBootstrapBlobWarmComplete,
  clearBootstrapBlobWarmComplete,
  auditBlobWarmCache,
  requestPersistentStorage,
} from './assetWarmup'
import type { AssetWarmEvent } from './assetWarmup'
import { postBlobGatePrecacheToServiceWorker } from './postBlobGatePrecacheToSw'

/** World-map TerrainPacker files + hex-battle rasters are fetched via `WORLD_MAP_*` + `getMvmHexBattlePrecacheUrls()` inside `VERCEL_BLOB_PRELOAD_BOOTSTRAP`; this only warms the SPA world-config API cache. */
function runWorldMapApiWarmupEarly(): void {
  warmWorldMapConfig()
}

/**
 * Hex battle Pixi atlases + elemental FX sheets — **not** part of cold boot.
 * Call from the land page (`warmupAssets`) so startup blob preload and first paint are not competing
 * with same-origin battle JSON + large texture decode. Idempotent via modules’ internal guards.
 */
export function scheduleDeferredPixiBattleAssetWarmup(): void {
  void import('../pages/hex-battle/mvmElementalPixiFx')
    .then((m) => {
      void m.preloadMvmElementalFxAssets()
      void m.preloadMvmHitBurstAssets()
      void m.preloadMvmGhostPuffAssets()
      void m.preloadMvmStatusIconAssets()
    })
    .catch((e) => console.warn('[deferred-warmup] elemental Pixi warmup:', e))

  void import('../mvmBattle/pixiBattleAssetPriming')
    .then((m) => void m.primePixiBattleAssetsEarly())
    .catch((e) => console.warn('[deferred-warmup] Pixi battle priming module:', e))
}

/**
 * Startup blob preload — URLs come from `src/constants/vercelBlobPreloadUrls.ts`.
 * **Gate:** all bootstrap URLs **except** TexturePacker atlas pairs (`.json` + sibling `.png`/`.webp`/… on the same stem)
 * so start page, land, modals, video, fonts, and other blobs fill the loading bar.
 * **Background:** remaining atlas JSON + sheets (`${fp}:atlases`), then map/river overlay atlases (`${fp}:map-overlay-atlases`).
 */

/** Loading-screen icon: must match an entry in `vercelBlobPreloadUrls.ts` (same CDN path). */
export const ECS_LOADING_ICON_URL =
  VERCEL_BLOB_PRELOAD_PRIORITIZED.find((u) => /\/Land\/ecs\.webp(\?|$)/i.test(u)) ??
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/eclipsera-gem.webp'

type ProgressListener = (loaded: number, total: number) => void

const listeners = new Set<ProgressListener>()
let inFlight: Promise<void> | null = null
let inFlightManifestFingerprint: string | null = null
let completedManifestFingerprint: string | null = null

/** One background pipeline per manifest (bootstrap atlases → deferred map overlays). */
let backgroundAtlasPipelineManifest: string | null = null

export type BackgroundAtlasWarmState =
  | { kind: 'idle' }
  | { kind: 'running'; phase: 'idle-assets' | 'lazy-assets'; loaded: number; total: number }

const backgroundAtlasWarmSubscribers = new Set<(s: BackgroundAtlasWarmState) => void>()

function emitBackgroundAtlasWarmState(s: BackgroundAtlasWarmState) {
  backgroundAtlasWarmSubscribers.forEach((fn) => {
    try {
      fn(s)
    } catch {
      /* ignore */
    }
  })
}

/** Subscribe to TexturePacker background warm progress (after the main loading bar). */
export function subscribeBackgroundAtlasWarmProgress(fn: (s: BackgroundAtlasWarmState) => void): () => void {
  backgroundAtlasWarmSubscribers.add(fn)
  return () => {
    backgroundAtlasWarmSubscribers.delete(fn)
  }
}

/**
 * True only after the gate manifest finished successfully (`preloadBlobUrlsWithProgress` wrote
 * `eclipsera.blobWarmComplete.v6.<fingerprint>`), or every gate URL was already in the service worker
 * caches on a later visit.
 *
 * Do **not** treat “attempt started” as done: closing the tab mid-load left the old code with only an
 * `attempted` flag and skipped preload forever despite empty HTTP cache.
 */
export function isVercelBlobGateWarmComplete(): boolean {
  const fp = VERCEL_BLOB_PRELOAD_MANIFEST_FINGERPRINT
  return completedManifestFingerprint === fp || isBootstrapBlobWarmComplete(fp)
}

/**
 * Delay before the background warm starts, measured from the end of the blocking gate.
 *
 * The land page issues its own requests as it mounts — and anything the player clicks in the
 * first seconds (a building, a modal) is an on-demand fetch. A background pass over thousands of
 * URLs starting immediately competes for the same connections and bandwidth, so those
 * user-initiated images arrive late enough to look broken. Let the page settle first.
 */
const BACKGROUND_WARMUP_DELAY_MS = 6000

/** Low enough to leave connections free for whatever the player actually clicks on. */
const BACKGROUND_WARMUP_CONCURRENCY = 3

/**
 * Higher than the background pass, because nothing competes with it: the player is looking at the
 * loading bar and the app is not making any other requests. The gate is ~900 mostly-small files
 * from one HTTP/2 origin, so the wall-clock cost is dominated by round trips rather than
 * bandwidth, and 6 in flight left the connection idle between them.
 */
const GATE_WARMUP_CONCURRENCY = 12

let backgroundWarmupScheduled = false

/** Start the background warm once the land page has had time to load and settle. */
export function scheduleBackgroundWarmupAfterFirstInteraction(): void {
  if (backgroundWarmupScheduled) return
  backgroundWarmupScheduled = true
  const start = () => startBootstrapAtlasBackgroundWarmup()
  if (typeof window === 'undefined') return
  window.setTimeout(() => {
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
      .requestIdleCallback
    if (typeof ric === 'function') ric(start, { timeout: 4000 })
    else start()
  }, BACKGROUND_WARMUP_DELAY_MS)
}

/**
 * Metered connection / Data Saver / 2G-3G. Warming tens of MB of "nice to have already there"
 * artwork over a phone plan is hostile, and on a slow link it competes with the requests the
 * player is actually waiting for. Those users get on-demand loading only.
 */
function shouldSkipBackgroundWarmup(): boolean {
  if (typeof navigator === 'undefined') return false
  const c = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string }
    }
  ).connection
  if (!c) return false
  if (c.saveData === true) return true
  return c.effectiveType === 'slow-2g' || c.effectiveType === '2g' || c.effectiveType === '3g'
}

/**
 * After the gate: warm the `idle` phase in the background so pages and modals are instant when
 * opened. Idempotent; skips work when `localStorage` warm keys are already set. Uses
 * partial-complete persistence so one bad URL does not force a full re-fetch on every refresh.
 *
 * The `lazy` phase (per-character portraits, battle videos — ~154 MB) is deliberately NOT warmed
 * here. A session touches a handful of those, so bulk-fetching them is pure waste; they load on
 * demand and `public/sw.js` caches each one the first time it is genuinely used.
 */
export function startBootstrapAtlasBackgroundWarmup(): void {
  const manifestFp = VERCEL_BLOB_PRELOAD_MANIFEST_FINGERPRINT
  if (backgroundAtlasPipelineManifest === manifestFp) return
  if (shouldSkipBackgroundWarmup()) {
    emitBackgroundAtlasWarmState({ kind: 'idle' })
    return
  }

  const atlasFp = `${manifestFp}:idle`
  const overlayFp = `${manifestFp}:lazy`
  const bootstrapAtlas = [...VERCEL_BLOB_PRELOAD_IDLE_URLS]
  const overlayAtlas: string[] = []

  const needBootstrap = bootstrapAtlas.length > 0 && !isBootstrapBlobWarmComplete(atlasFp)
  const needOverlay = overlayAtlas.length > 0 && !isBootstrapBlobWarmComplete(overlayFp)
  if (!needBootstrap && !needOverlay) {
    emitBackgroundAtlasWarmState({ kind: 'idle' })
    return
  }

  const grandTotal =
    (needBootstrap ? bootstrapAtlas.length : 0) + (needOverlay ? overlayAtlas.length : 0)
  const bootstrapCount = needBootstrap ? bootstrapAtlas.length : 0

  backgroundAtlasPipelineManifest = manifestFp

  void (async () => {
    try {
      emitBackgroundAtlasWarmState({
        kind: 'running',
        phase: needBootstrap ? 'idle-assets' : 'lazy-assets',
        loaded: 0,
        total: grandTotal,
      })
      if (needBootstrap) {
        await preloadBlobUrlsWithProgress(
          bootstrapAtlas,
          (l) => {
            emitBackgroundAtlasWarmState({
              kind: 'running',
              phase: 'idle-assets',
              loaded: l,
              total: grandTotal,
            })
          },
          BACKGROUND_WARMUP_CONCURRENCY,
          atlasFp,
          // Speculative: nothing is about to paint these, so skip the throwaway decode.
          { allowPartialComplete: true, decodeImages: false },
        )
      }
      if (needOverlay) {
        await preloadBlobUrlsWithProgress(
          overlayAtlas,
          (l) => {
            emitBackgroundAtlasWarmState({
              kind: 'running',
              phase: 'lazy-assets',
              loaded: bootstrapCount + l,
              total: grandTotal,
            })
          },
          4,
          overlayFp,
          { allowPartialComplete: true },
        )
      }
    } catch (e) {
      console.warn('[background-atlas-warm] unexpected error', e)
    } finally {
      emitBackgroundAtlasWarmState({ kind: 'idle' })
      if (backgroundAtlasPipelineManifest === manifestFp) {
        backgroundAtlasPipelineManifest = null
      }
    }
  })()
}

function notify(loaded: number, total: number) {
  listeners.forEach((fn) => {
    try {
      fn(loaded, total)
    } catch {
      /* ignore */
    }
  })
}

/* ────────────────────────── live gate detail (what is loading right now) ───────────────────── */

/**
 * Raw loading detail, for the `?debugload=1` view only.
 *
 * The player-facing status line is written copy in `constants/loadingMessages.ts` and is chosen by
 * `GameLoadingScreen`, not derived from this. Naming the file currently in flight was useful for
 * diagnosing a slow start and wrong for anyone else — so it stays, behind the flag.
 */
export type GatePreloadDetail = {
  /** Folder the current asset lives in, e.g. `characters-front-view-new`. Debug view only. */
  group: string
  /** File name without extension, e.g. `rena`. Debug view only. */
  name: string
  /** How many warms are in flight right now (should sit at the configured concurrency). */
  inFlight: number
  /** Slowest completed assets so far, worst first. This is the debugging payload. */
  slowest: readonly { label: string; ms: number }[]
}

/** `?debugload=1` swaps the status line for raw asset paths, in-flight count and slowest-so-far. */
export function isLoadDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (new URLSearchParams(window.location.search).get('debugload') === '1') return true
    return window.localStorage.getItem('eclipsera.debugLoad') === '1'
  } catch {
    return false
  }
}

const detailListeners = new Set<(d: GatePreloadDetail) => void>()

/** Keep the worst N. Small: it is rendered, and a longer list tells you nothing more. */
const SLOWEST_TRACKED = 8

/**
 * Live detail is a debugging aid, so it must not become the reason loading is slow. Events fire
 * ~1900 times over a cold gate; at one React render each that is a measurable tax on the very
 * thing being measured. Coalesce to this interval instead.
 */
const DETAIL_EMIT_INTERVAL_MS = 120

/** Split a CDN URL into the two parts worth putting on screen. */
function describeAssetUrl(url: string): { group: string; name: string } {
  let path = url
  try {
    path = new URL(url).pathname
  } catch {
    /* keep the raw string */
  }
  const segments = path.split('/').filter(Boolean)
  const file = segments.pop() ?? url
  const name = decodeURIComponent(file.replace(/\.[a-z0-9]+$/i, ''))
  const group = decodeURIComponent(segments.pop() ?? '')
  return { group, name }
}

type SlowEntry = { label: string; ms: number }

/**
 * Tracks what the gate is doing and pushes a throttled snapshot to the loading screen.
 *
 * Deliberately reports the asset that started most recently rather than trying to render all
 * twelve in flight: with this much concurrency a full list is unreadable churn, while a single
 * rolling name plus the in-flight count conveys both "it is moving" and "it is saturated".
 */
function createGateDetailReporter() {
  const slowest: SlowEntry[] = []
  let current: { group: string; name: string } = { group: '', name: '' }
  let inFlight = 0
  let lastEmit = 0
  let pending = false

  const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

  const flush = () => {
    pending = false
    lastEmit = now()
    const snapshot: GatePreloadDetail = {
      group: current.group,
      name: current.name,
      inFlight,
      slowest: slowest.slice(),
    }
    detailListeners.forEach((fn) => {
      try {
        fn(snapshot)
      } catch {
        /* a broken listener must not stop the warm */
      }
    })
  }

  const schedule = () => {
    if (pending) return
    const wait = Math.max(0, DETAIL_EMIT_INTERVAL_MS - (now() - lastEmit))
    pending = true
    window.setTimeout(flush, wait)
  }

  const recordSlow = (url: string, ms: number) => {
    if (slowest.length >= SLOWEST_TRACKED && ms <= slowest[slowest.length - 1].ms) return
    const { group, name } = describeAssetUrl(url)
    slowest.push({ label: group ? `${group}/${name}` : name, ms })
    slowest.sort((a, b) => b.ms - a.ms)
    if (slowest.length > SLOWEST_TRACKED) slowest.length = SLOWEST_TRACKED
  }

  return {
    onAssetEvent(e: AssetWarmEvent) {
      inFlight = e.inFlight
      if (e.kind === 'start') {
        current = describeAssetUrl(e.url)
      } else {
        recordSlow(e.url, e.ms)
      }
      schedule()
    },
    slowest(): readonly SlowEntry[] {
      return slowest
    },
    /** Final paint, bypassing the throttle, so the screen never freezes on a stale name. */
    finish() {
      current = { group: '', name: '' }
      inFlight = 0
      flush()
    },
  }
}

/** Subscribe to what the gate is currently loading. No-op once the gate has finished. */
export function subscribeGatePreloadDetail(fn: (d: GatePreloadDetail) => void): () => void {
  detailListeners.add(fn)
  return () => {
    detailListeners.delete(fn)
  }
}

/**
 * Single shared preload for the session (survives React StrictMode double mount).
 * URL list is defined in `vercelBlobPreloadUrls.ts`. Repeat visits bail out only when
 * `isBootstrapBlobWarmComplete(manifestFingerprint)` is true (see `assetWarmup.ts`).
 * While running, each URL is verified via `preloadBlobUrlsWithProgress` (full body read + image decode); the service worker may satisfy `fetch` from Cache Storage without a network round-trip.
 *
 * `public/sw.js` may store blob responses in Cache Storage **after** the app calls `enableVercelBlobServiceWorkerCache()`
 * (when the loading screen / bootstrap finishes), so startup `fetch` preload does not tie into SW cache.
 * Until then, blob CDN requests are passthrough `fetch` (HTTP cache still applies).
 * Startup preload skips `/avatars/*` (loaded when UI needs them).
 */
export function ensureVercelBlobPreload(): Promise<void> {
  const fp = VERCEL_BLOB_PRELOAD_MANIFEST_FINGERPRINT
  if (completedManifestFingerprint === fp || isBootstrapBlobWarmComplete(fp)) {
    completedManifestFingerprint = fp
    return Promise.resolve()
  }
  if (inFlight && inFlightManifestFingerprint === fp) return inFlight

  const atlasFp = `${fp}:idle`
  const gateUrls = [...VERCEL_BLOB_PRELOAD_BOOTSTRAP_GATE_URLS]
  const atlasUrls = [...VERCEL_BLOB_PRELOAD_IDLE_URLS]

  runWorldMapApiWarmupEarly()

  /**
   * Turn on the service worker blob cache BEFORE any warming, not after.
   *
   * Previously this was only called once bootstrap reached `ready`, so the entire preload —
   * by far the biggest download the app performs — bypassed Cache Storage and landed only in
   * the evictable HTTP cache. Enabling first means every warmed response is written to Cache
   * Storage on its way through `public/sw.js`, which is what makes a reopen actually warm.
   */
  enableVercelBlobServiceWorkerCache()
  void requestPersistentStorage()

  inFlightManifestFingerprint = fp

  inFlight = (async () => {
    /**
     * The gate is the longest thing the app does, and "it feels slow" is not something you can
     * act on. This prints how long it actually took, how many assets were already cached, and the
     * effective rate — enough to tell a slow connection apart from a stalled pipeline without
     * attaching a profiler. `performance.now()` so it is unaffected by clock changes.
     */
    const startedAt = typeof performance !== 'undefined' ? performance.now() : 0
    let firstProgressAt = 0
    let alreadyCached = 0
    const timedNotify = (loaded: number, total: number) => {
      if (firstProgressAt === 0 && typeof performance !== 'undefined') {
        firstProgressAt = performance.now()
        alreadyCached = loaded
      }
      notify(loaded, total)
    }
    const detail = createGateDetailReporter()
    const logGateTiming = () => {
      detail.finish()
      if (typeof performance === 'undefined') return
      const totalMs = performance.now() - startedAt
      const scanMs = firstProgressAt > 0 ? firstProgressAt - startedAt : 0
      const fetched = gateUrls.length - alreadyCached
      console.info('[blob-warm] gate complete', {
        seconds: +(totalMs / 1000).toFixed(1),
        urls: gateUrls.length,
        alreadyCached,
        fetched,
        cacheScanSeconds: +(scanMs / 1000).toFixed(2),
        assetsPerSecond: totalMs > 0 ? +((fetched / totalMs) * 1000).toFixed(1) : 0,
        concurrency: GATE_WARMUP_CONCURRENCY,
      })
      const slowest = detail.slowest()
      if (slowest.length > 0) {
        console.info(
          '[blob-warm] slowest gate assets',
          slowest.map((s) => ({ asset: s.label, seconds: +(s.ms / 1000).toFixed(2) })),
        )
      }
    }

    try {
      if (gateUrls.length > 0) {
        await preloadBlobUrlsWithProgress(gateUrls, timedNotify, GATE_WARMUP_CONCURRENCY, fp, {
          allowPartialComplete: true,
          /**
           * Decoding proved each image was valid, then closed the bitmap and threw the result
           * away — the browser decodes again when the image actually paints. At 374 gated assets
           * that was tolerable; at ~940 it is a second full decode pass over ~80 MB of artwork,
           * competing with the downloads for CPU and allocating tens of MB of RGBA per batch.
           * Cheap phones felt this more than the network did.
           *
           * What the gate needs is the bytes in Cache Storage, which the full body read already
           * guarantees. A corrupt file now surfaces when it paints rather than during the warm.
           */
          decodeImages: false,
          onAssetEvent: detail.onAssetEvent,
        })
        logGateTiming()
        // Backfill: anything fetched before the SW took control of the page never passed
        // through its fetch handler, so ask the worker to store those itself.
        void navigator.serviceWorker?.ready
          .then((reg) => postBlobGatePrecacheToServiceWorker(reg))
          .catch(() => {})
        scheduleBackgroundWarmupAfterFirstInteraction()
      } else if (atlasUrls.length > 0) {
        await preloadBlobUrlsWithProgress(atlasUrls, notify, 6, atlasFp)
        startBootstrapAtlasBackgroundWarmup()
      } else if (VERCEL_BLOB_PRELOAD_LAZY_URLS.length > 0) {
        startBootstrapAtlasBackgroundWarmup()
      }
      completedManifestFingerprint = fp
      if (inFlightManifestFingerprint === fp) {
        inFlightManifestFingerprint = null
        inFlight = null
      }
    } catch (e) {
      if (inFlightManifestFingerprint === fp) {
        inFlightManifestFingerprint = null
      }
      inFlight = null
      throw e
    }
  })()

  return inFlight
}

let verifiedGateManifest: string | null = null
let gateVerification: Promise<boolean> | null = null

/**
 * Whether the persisted "gate warm complete" flag can be trusted for THIS page load.
 *
 * The flag only records that a warm loop once ran to completion — not that the bytes survived,
 * and not that this page can reach them. Storage eviction leaves the flag set and the cache
 * empty; a hard reload ("Empty cache and hard reload", DevTools bypass) leaves Cache Storage full
 * but loads the page past the service worker, so every `<img>` goes to the network anyway. Both
 * are exactly the state where the app used to skip the loading screen and then fetch every asset
 * lazily underneath a live UI.
 *
 * Resolves `true` when every gate URL (minus those the CDN reports gone) is reachable from a
 * cache this page will actually be served from. Otherwise the flag is dropped so the next
 * `ensureVercelBlobPreload()` runs for real — behind the loading screen — and only the missing
 * assets are fetched, since the warm skips what is already cached. Runs once per page load.
 *
 * If the audit itself cannot run (Cache Storage unreadable), the flag is trusted as before:
 * "could not verify" must not turn into a loading screen on every boot.
 */
export function verifyVercelBlobGateWarm(): Promise<boolean> {
  const fp = VERCEL_BLOB_PRELOAD_MANIFEST_FINGERPRINT
  if (verifiedGateManifest === fp) return Promise.resolve(true)
  if (gateVerification) return gateVerification
  if (!isVercelBlobGateWarmComplete()) return Promise.resolve(false)

  gateVerification = (async () => {
    try {
      const gateUrls = [...VERCEL_BLOB_PRELOAD_BOOTSTRAP_GATE_URLS]
      const audit = await auditBlobWarmCache(gateUrls, fp)
      if (audit.missing === 0) {
        verifiedGateManifest = fp
        return true
      }
      /**
       * The audit could not read Cache Storage, so `missing` is an assumption. Trust the persisted
       * flag and hand the app over: a page that really is cold will fault its assets in on demand,
       * which is far cheaper than a 981-URL re-download triggered by an unreadable cache.
       *
       * Critically, the warm flags are NOT cleared here. Clearing on an unverified miss is what
       * turned a single bad read into a permanent loop — the cleared flag made the next boot cold,
       * which re-ran the gate, which cleared it again.
       */
      if (!audit.verified) {
        console.warn(
          '[blob-warm] gate audit could not read Cache Storage — trusting the persisted warm flag ' +
            'and leaving it intact (assets will load on demand if genuinely absent)',
        )
        verifiedGateManifest = fp
        return true
      }
      console.info(
        `[blob-warm] gate audit: ${audit.missing}/${audit.total} assets missing from Cache Storage` +
          (audit.swControlled ? '' : ' (page is not controlled by the service worker)') +
          ' — re-running the gate behind the loading screen',
      )
      clearBootstrapBlobWarmComplete(fp)
      // The background sweep's own flag lies for the same reason; let it run again too.
      clearBootstrapBlobWarmComplete(`${fp}:idle`)
      completedManifestFingerprint = null
      return false
    } catch (e) {
      console.warn('[blob-warm] gate audit could not run; trusting the persisted warm flag', e)
      verifiedGateManifest = fp
      return true
    } finally {
      gateVerification = null
    }
  })()
  return gateVerification
}

/** Tell the active service worker to start using Cache Storage for Vercel blob CDN (audio + images). Call once after bootstrap loading completes — see `VercelAssetBootstrap` `ready` phase. */
export function enableVercelBlobServiceWorkerCache(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  const msg = { type: 'eclipsera-enable-vercel-blob-cache' as const }
  const send = (): boolean => {
    const c = navigator.serviceWorker.controller
    if (c) {
      c.postMessage(msg)
      return true
    }
    return false
  }
  if (send()) return
  void navigator.serviceWorker.ready.then((reg) => {
    try {
      reg.active?.postMessage(msg)
    } catch {
      /* ignore */
    }
    if (send()) return
    const onController = () => {
      void send()
    }
    navigator.serviceWorker.addEventListener('controllerchange', onController, { once: true })
  })
}

export function subscribeVercelBlobPreloadProgress(fn: ProgressListener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}
